import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  AlertDescription,
  AlertIcon,
  Box,
  Button,
  Card,
  CardBody,
  FormControl,
  FormLabel,
  Heading,
  HStack,
  IconButton,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  Spinner,
  Table,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  useBreakpointValue,
  useColorModeValue,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { Link as RouterLink } from 'react-router-dom';
import { FaEye, FaPencilAlt, FaPlus, FaUserPlus } from 'react-icons/fa';
import {
  createConsultantOrganization,
  findTempPassword,
  formatOrganizationLocation,
  getConsultantNetworks,
  getConsultantOrganizations,
  attachOrganizationToNetwork,
} from './api';
import OrganizationGeneralFields from '../organization/OrganizationGeneralFields';
import OrganizationBillingFields from '../organization/OrganizationBillingFields';
import ContextUserCreateModal from '../users/ContextUserCreateModal';
import PaginatedListToolbar from '../common/PaginatedListToolbar';
import PaginationControls from '../common/PaginationControls';
import usePaginatedList from '../common/usePaginatedList';

const emptyOrganization = {
  name: '',
  contact: '',
  title: '',
  email: '',
  phone: '',
  address: '',
  city: '',
  state: '',
  zip: '',
  country: '',
  notes: '',
  billing_same_as_org: true,
  billing_company_name: '',
  billing_address: '',
  billing_zip: '',
  billing_city: '',
  billing_state: '',
  billing_country: '',
  vat_or_tax_number: '',
  currency: '',
  billing_method: '',
  billing_email: '',
  billing_method_other: '',
  reference_person: '',
  reference_number: '',
  cost_center: '',
  billing_notes: '',
  peppol: '',
  gln: '',
  organisationsnummer: '',
};
const emptyAttach = { organization_id: '', network_id: '' };

const organizationUserLabel = (organization) => {
  const user = organization?.user;
  if (!user) return 'No user assigned';
  const fullName = [user.first_name, user.last_name].filter(Boolean).join(' ').trim();
  return fullName ? `${fullName} (${user.email || 'No email'})` : (user.email || 'User assigned');
};

const organizationNetworksLabel = (organization) => {
  const networks = Array.isArray(organization?.networks) ? organization.networks : [];
  if (networks.length === 0) return 'None';
  return networks.map((network) => network.name).join(', ');
};

const canAttachOrganizationToNetwork = (organization) => {
  const networkCount = Number(organization?.network_count || 0);
  return networkCount === 0;
};

const ConsultantOrganizations = () => {
  const toast = useToast();
  const isMobile = useBreakpointValue({ base: true, md: false });
  const cardBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');

  const [isLoading, setIsLoading] = useState(true);
  const [organizations, setOrganizations] = useState([]);
  const [networks, setNetworks] = useState([]);
  const [banner, setBanner] = useState(null);

  const [organizationForm, setOrganizationForm] = useState(emptyOrganization);
  const [attachForm, setAttachForm] = useState(emptyAttach);
  const [selectedOrganizationForUser, setSelectedOrganizationForUser] = useState(null);

  const [isSubmittingOrg, setIsSubmittingOrg] = useState(false);
  const [isSubmittingAttach, setIsSubmittingAttach] = useState(false);

  const orgModal = useDisclosure();
  const orgUserModal = useDisclosure();
  const attachModal = useDisclosure();

  const loadData = useCallback(async () => {
    setIsLoading(true);
    try {
      const [organizationsPayload, networksPayload] = await Promise.all([
        getConsultantOrganizations(),
        getConsultantNetworks(),
      ]);
      setOrganizations(organizationsPayload);
      setNetworks(networksPayload);
    } catch (error) {
      console.error('Error loading consultant organizations:', error);
      toast({ title: 'Could not load schools', status: 'error', duration: 4000, isClosable: true });
    } finally {
      setIsLoading(false);
    }
  }, [toast]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const organizationSortOptions = useMemo(
    () => [
      { key: 'name', label: 'Name', getSortValue: (organization) => organization?.name },
      {
        key: 'location',
        label: 'Location',
        getSortValue: (organization) => formatOrganizationLocation(organization),
      },
      { key: 'user', label: 'User', getSortValue: organizationUserLabel },
      { key: 'network', label: 'Network', getSortValue: organizationNetworksLabel },
    ],
    []
  );

  const {
    paginatedItems: paginatedOrganizations,
    totalCount,
    totalPages,
    currentPage,
    pageSize,
    searchText,
    sortKey,
    sortDir,
    setCurrentPage,
    setPageSize,
    setSearchText,
    setSortDir,
    setSortKey,
  } = usePaginatedList({
    items: organizations,
    sortOptions: organizationSortOptions,
    initialSortKey: 'name',
    initialSortDir: 'asc',
    initialPageSize: 25,
    searchMatcher: (organization, normalizedSearch) =>
      [
        organization?.name,
        formatOrganizationLocation(organization),
        organizationUserLabel(organization),
        organizationNetworksLabel(organization),
      ].some((value) => value?.toString().toLowerCase().includes(normalizedSearch)),
  });

  const handleCreateOrganization = async (event) => {
    event.preventDefault();
    setIsSubmittingOrg(true);
    try {
      await createConsultantOrganization(organizationForm);
      toast({ title: 'School created', status: 'success', duration: 3000, isClosable: true });
      setOrganizationForm(emptyOrganization);
      orgModal.onClose();
      await loadData();
    } catch (error) {
      console.error('Error creating organization:', error);
      toast({ title: 'Could not create school', status: 'error', duration: 4000, isClosable: true });
    } finally {
      setIsSubmittingOrg(false);
    }
  };

  const handleAttachOrganization = async (event) => {
    event.preventDefault();
    setIsSubmittingAttach(true);
    try {
      await attachOrganizationToNetwork(attachForm.network_id, attachForm.organization_id);
      toast({ title: 'School attached', status: 'success', duration: 3000, isClosable: true });
      setAttachForm(emptyAttach);
      attachModal.onClose();
      await loadData();
    } catch (error) {
      console.error('Error attaching organization:', error);
      toast({ title: 'Could not attach school', status: 'error', duration: 4000, isClosable: true });
    } finally {
      setIsSubmittingAttach(false);
    }
  };

  const openOrgUserModalFor = (organization) => {
    setSelectedOrganizationForUser(organization);
    orgUserModal.onOpen();
  };

  const openAttachModalFor = (organization) => {
    setAttachForm((prev) => ({ ...prev, organization_id: organization.id }));
    attachModal.onOpen();
  };

  return (
    <Box p={{ base: 4, md: 6 }}>
      <HStack justify="space-between" align={{ base: 'start', md: 'center' }} mb={4} flexWrap="wrap">
        <Box>
          <Heading size="lg">Schools</Heading>
          <Text color="gray.500">Manage and onboard schools.</Text>
        </Box>
        <Button leftIcon={<FaPlus />} colorScheme="blue" variant="outline" onClick={orgModal.onOpen}>
          Create school
        </Button>
      </HStack>

      {banner && (
        <Alert status="success" borderRadius="md" mb={4}>
          <AlertIcon />
          <AlertDescription>{banner}</AlertDescription>
        </Alert>
      )}

      <Card bg={cardBg} borderWidth="1px" borderColor={borderColor} mb={4}>
        <CardBody>
          <PaginatedListToolbar
            searchText={searchText}
            onSearchTextChange={setSearchText}
            searchPlaceholder="Search by name, location, user, or network"
            pageSize={pageSize}
            onPageSizeChange={setPageSize}
            totalCount={totalCount}
            sortKey={sortKey}
            sortDir={sortDir}
            sortOptions={organizationSortOptions}
            onSortKeyChange={setSortKey}
            onSortDirChange={setSortDir}
          />
        </CardBody>
      </Card>

      {isLoading ? (
        <HStack justify="center" py={12}><Spinner size="lg" /></HStack>
      ) : totalCount === 0 ? (
        <Card bg={cardBg} borderWidth="1px" borderColor={borderColor}>
          <CardBody>
            <Text>No schools found.</Text>
          </CardBody>
        </Card>
      ) : isMobile ? (
        <VStack align="stretch" spacing={3}>
          {paginatedOrganizations.map((organization) => (
            <Card key={organization.id} bg={cardBg} borderWidth="1px" borderColor={borderColor}>
              <CardBody>
                <VStack align="stretch" spacing={3}>
                  <Box>
                    <Text
                      as={RouterLink}
                      to={`/app/consultant/organizations/${organization.id}`}
                      fontWeight="semibold"
                      color="blue.600"
                    >
                      {organization.name}
                    </Text>
                    <Text fontSize="sm" color="gray.500">{formatOrganizationLocation(organization)}</Text>
                    <Text fontSize="sm" color="gray.600" mt={2}>User: {organizationUserLabel(organization)}</Text>
                    <Text fontSize="sm" color="gray.600">Network: {organizationNetworksLabel(organization)}</Text>
                  </Box>
                  <HStack flexWrap="wrap">
                    <Button as={RouterLink} to={`/app/consultant/organizations/${organization.id}`} size="sm" variant="outline" leftIcon={<FaEye />}>
                      View
                    </Button>
                    <Button as={RouterLink} to={`/app/consultant/organizations/${organization.id}/edit`} size="sm" variant="outline" leftIcon={<FaPencilAlt />}>
                      Edit
                    </Button>
                    <Button size="sm" variant="outline" leftIcon={<FaUserPlus />} onClick={() => openOrgUserModalFor(organization)}>
                      Add user
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => openAttachModalFor(organization)}
                      isDisabled={!canAttachOrganizationToNetwork(organization)}
                    >
                      {canAttachOrganizationToNetwork(organization) ? 'Attach to network' : 'Already in network'}
                    </Button>
                  </HStack>
                </VStack>
              </CardBody>
            </Card>
          ))}
        </VStack>
      ) : (
        <Card bg={cardBg} borderWidth="1px" borderColor={borderColor}>
          <CardBody>
            <TableContainer>
              <Table>
                <Thead>
                  <Tr>
                    <Th>Name</Th>
                    <Th>Location</Th>
                    <Th>User</Th>
                    <Th>Network</Th>
                    <Th>Actions</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {paginatedOrganizations.map((organization) => (
                    <Tr key={organization.id}>
                      <Td fontWeight="semibold">
                        <Text
                          as={RouterLink}
                          to={`/app/consultant/organizations/${organization.id}`}
                          color="blue.600"
                        >
                          {organization.name}
                        </Text>
                      </Td>
                      <Td>{formatOrganizationLocation(organization)}</Td>
                      <Td>{organizationUserLabel(organization)}</Td>
                      <Td>{organizationNetworksLabel(organization)}</Td>
                      <Td>
                        <HStack>
                          <IconButton as={RouterLink} to={`/app/consultant/organizations/${organization.id}/edit`} icon={<FaPencilAlt />} aria-label="Edit school" size="sm" variant="outline" />
                          <IconButton
                            icon={<FaUserPlus />}
                            aria-label="Add school user"
                            size="sm"
                            variant="outline"
                            onClick={() => openOrgUserModalFor(organization)}
                          />
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => openAttachModalFor(organization)}
                            isDisabled={!canAttachOrganizationToNetwork(organization)}
                          >
                            {canAttachOrganizationToNetwork(organization) ? 'Attach to network' : 'Already in network'}
                          </Button>
                        </HStack>
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </TableContainer>
          </CardBody>
        </Card>
      )}
      {!isLoading && totalCount > 0 ? (
        <PaginationControls
          currentPage={currentPage}
          totalPages={totalPages}
          onPageChange={setCurrentPage}
        />
      ) : null}

      <Modal size="5xl" isOpen={orgModal.isOpen} onClose={orgModal.onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Create school</ModalHeader>
          <ModalCloseButton />
          <form onSubmit={handleCreateOrganization}>
            <ModalBody>
              <VStack spacing={4} maxH="65vh" overflowY="auto" align="stretch">
                <OrganizationGeneralFields
                  values={organizationForm}
                  onChange={(event) => {
                    const { name, value } = event.target;
                    setOrganizationForm((prev) => ({ ...prev, [name]: value }));
                  }}
                  sectionBg="gray.50"
                />
                <OrganizationBillingFields
                  values={organizationForm}
                  onChange={(event) => {
                    const { name, value } = event.target;
                    setOrganizationForm((prev) => ({ ...prev, [name]: value }));
                  }}
                  onBillingSameAsOrgChange={(value) =>
                    setOrganizationForm((prev) => ({ ...prev, billing_same_as_org: value }))
                  }
                  sectionBg="gray.50"
                />
              </VStack>
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={orgModal.onClose}>Cancel</Button>
              <Button type="submit" colorScheme="blue" isLoading={isSubmittingOrg}>Create</Button>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>

      <ContextUserCreateModal
        isOpen={orgUserModal.isOpen}
        onClose={() => {
          orgUserModal.onClose();
          setSelectedOrganizationForUser(null);
        }}
        context={{ type: 'organization', id: Number(selectedOrganizationForUser?.id), namespace: 'consultant' }}
        title={`Create user${selectedOrganizationForUser?.name ? ` for ${selectedOrganizationForUser.name}` : ''}`}
        submitLabel="Create user"
        onCreated={async (response, payload) => {
          const tempPassword = findTempPassword(response?.data);
          setBanner(
            tempPassword
              ? `Temporary password for ${payload.email}: ${tempPassword}`
              : `Access email sent to ${payload.email}.`
          );
          await loadData();
        }}
      />

      <Modal isOpen={attachModal.isOpen} onClose={attachModal.onClose}>
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>Attach to network</ModalHeader>
          <ModalCloseButton />
          <form onSubmit={handleAttachOrganization}>
            <ModalBody>
              <VStack spacing={3}>
                <FormControl isRequired>
                  <FormLabel>School</FormLabel>
                  <Select value={attachForm.organization_id} onChange={(e) => setAttachForm((prev) => ({ ...prev, organization_id: e.target.value }))}>
                    <option value="">Select school</option>
                    {organizations.map((organization) => (
                      <option key={organization.id} value={organization.id}>{organization.name}</option>
                    ))}
                  </Select>
                </FormControl>
                <FormControl isRequired>
                  <FormLabel>Network</FormLabel>
                  <Select value={attachForm.network_id} onChange={(e) => setAttachForm((prev) => ({ ...prev, network_id: e.target.value }))}>
                    <option value="">Select network</option>
                    {networks.map((network) => (
                      <option key={network.id} value={network.id}>{network.name}</option>
                    ))}
                  </Select>
                </FormControl>
              </VStack>
            </ModalBody>
            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={attachModal.onClose}>Cancel</Button>
              <Button type="submit" colorScheme="blue" isLoading={isSubmittingAttach} isDisabled={!attachForm.network_id || !attachForm.organization_id}>Attach</Button>
            </ModalFooter>
          </form>
        </ModalContent>
      </Modal>
    </Box>
  );
};

export default ConsultantOrganizations;
